import { useState, useEffect, useRef, useCallback } from 'react';

const TOTAL_CELLS = 20;
const CELL_W = 32;
const CELL_H = 8;
const GAP = 8;

export default function ScrollBatteryBar() {
  const [progress, setProgress] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = docHeight > 0 ? scrollTop / docHeight : 0;
      setProgress(ratio);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToRatio = useCallback((ratio: number) => {
    const docHeight = document.documentElement.scrollHeight - window.innerHeight;
    const targetY = Math.max(0, Math.min(1, ratio)) * docHeight;
    window.scrollTo({ top: targetY, behavior: 'auto' });
  }, []);

  const getRatioFromEvent = useCallback((clientY: number) => {
    const el = containerRef.current;
    if (!el) return 0;
    const rect = el.getBoundingClientRect();
    const relY = clientY - rect.top;
    return Math.max(0, Math.min(1, relY / rect.height));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    scrollToRatio(getRatioFromEvent(e.clientY));

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      scrollToRatio(getRatioFromEvent(ev.clientY));
    };
    const onMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
  }, [getRatioFromEvent, scrollToRatio]);

  const handleCellClick = useCallback((index: number) => {
    const ratio = TOTAL_CELLS <= 1 ? 0 : index / (TOTAL_CELLS - 1);
    scrollToRatio(ratio);
  }, [scrollToRatio]);

  const filled = Math.round(progress * TOTAL_CELLS);

  // 鼠标悬浮时，悬浮格子及以上全部亮起（预览效果）
  const getHoverFilled = () => {
    if (hoveredIndex === null) return -1;
    return hoveredIndex + 1;
  };
  const hoverFilled = getHoverFilled();

  const getCellStyle = (i: number) => {
    const isFilledByScroll = i < filled;
    const isFilledByHover = hoveredIndex !== null && i <= hoveredIndex;

    if (isFilledByHover) {
      if (isFilledByScroll) {
        return 'bg-[#5ab0d8] border-[#5ab0d8] shadow-[0_0_10px_4px_rgba(0,136,204,0.35)]';
      }
      return 'bg-[#0088CC]/30 border-[#0088CC]/50';
    }

    if (isFilledByScroll) {
      return 'bg-[#0088CC]/70 border-[#0088CC]/80 shadow-[0_0_6px_2px_rgba(0,136,204,0.25)]';
    }

    return 'bg-transparent border-[#b8d4ea]/60';
  };

  return (
    <div
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseLeave={() => setHoveredIndex(null)}
      className="fixed right-3 top-[58%] -translate-y-1/2 z-50 flex flex-col items-end p-2 rounded-xl cursor-pointer select-none bg-white/30 backdrop-blur-md border border-[#b8d4ea]/40"
      style={{ gap: GAP }}
    >
      {Array.from({ length: TOTAL_CELLS }).map((_, i) => (
        <button
          key={i}
          onClick={() => handleCellClick(i)}
          onMouseEnter={() => setHoveredIndex(i)}
          style={{ width: CELL_W, height: CELL_H }}
          className={`block rounded-[2px] border transition-all duration-100 cursor-pointer ${getCellStyle(i)}`}
        />
      ))}
    </div>
  );
}
